/**
 * TURBO MOTO 84 — instance.
 *
 * Endless-survival motocross. The track scrolls right-to-left forever and
 * ramps up with distance. The rider hops between 4 horizontal lanes to
 * dodge obstacles and line up ramps; on a ramp the bike launches and the
 * player tilts nose up/down to control the landing angle. A clean flat
 * landing banks speed + points; a bad angle wipes out.
 *
 * The signature mechanic is HEAT: holding BOOST (fireB) accelerates but
 * the engine temperature climbs. Redline it and the engine STALLS — the
 * bike bogs down and can't boost for a beat while it cools. Temperature
 * bleeds off whenever boost is released. This is a soft failure (a
 * tempo-killer), distinct from the hard failure of a crash.
 *
 * Score = distance (primary) + clean-landing and pass bonuses. Run ends
 * on a fatal crash. Deterministic: all randomness via ctx.random, so the
 * server validator can replay a run byte-for-byte.
 *
 * KEEP IN SYNC with the remaster (apps/games/turbo-moto/src/main.js) —
 * same lane count, heat economy, and scoring intent.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
  LoopHandle,
} from '@arcraz/arcadia-types';
import {
  ATTRACT_PROMPT,
  ATTRACT_TITLE,
  CARD_BG,
  CARD_BORDER,
  CARD_SUB,
  CARD_TEXT,
  CRASH_FLASH,
  HEAT_COOL,
  HEAT_FRAME,
  HEAT_HOT,
  HEAT_REDLINE,
  HEAT_WARM,
  HILL_FAR,
  HILL_NEAR,
  HUD_DIST,
  HUD_LABEL,
  HUD_SCORE,
  HUD_SPEED,
  LANE_LINE,
  MUD,
  RAMP_EDGE,
  RAMP_FACE,
  RIDER_BODY,
  RIDER_DARK,
  RIDER_HELMET,
  RIDER_TRAIL,
  RIDER_WHEEL,
  RIVAL_BODY,
  RIVAL_DARK,
  RIVAL_HELMET,
  ROCK,
  ROCK_DARK,
  SKY_BOTTOM,
  SKY_TOP,
  SUN,
  TRACK_BASE,
  TRACK_DARK,
  TRACK_EDGE,
  TRACK_STRIPE,
} from './palette.js';

const WIDTH = 480;
const HEIGHT = 270;

// ---- Playfield geometry -----------------------------------------------------
// The track occupies a horizontal band; 4 lanes stack within it. Sky above,
// a thin HUD strip is painted over the top.
const TRACK_TOP = 96;
const TRACK_BOTTOM = 250;
const LANE_COUNT = 4;
const LANE_H = (TRACK_BOTTOM - TRACK_TOP) / LANE_COUNT;
/** The rider is pinned near the left; the world scrolls toward it. */
const RIDER_X = 96;

// ---- Speed / distance -------------------------------------------------------
// World units are "meters"; scroll speed is meters/second. dt is 1/60.
const BASE_SPEED = 26; // cruising m/s off-boost
const BOOST_SPEED = 44; // m/s while boosting (and not overheated)
const STALL_SPEED = 12; // m/s the engine bogs to during an overheat stall
const ACCEL = 40; // m/s^2 toward the target speed
/** Difficulty scales with distance; caps so it stays humanly survivable. */
const RAMP_DIST = 2600; // meters over which difficulty climbs to full
const MAX_DIFF = 1;

// ---- Heat economy (the signature mechanic) ---------------------------------
// KEEP IN SYNC with the remaster.
const HEAT_MAX = 100;
const HEAT_RISE_PER_S = 34; // while boosting
const HEAT_COOL_PER_S = 22; // while off boost
const STALL_HEAT_CLEAR = 45; // stall ends once heat bleeds below this
const STALL_MIN_FRAMES = 30; // a stall lasts at least ~0.5s

// ---- Lane hop ---------------------------------------------------------------
const HOP_FRAMES = 9; // frames to slide between adjacent lanes

// ---- Jump / landing ---------------------------------------------------------
const GRAVITY = 62; // vertical units / s^2 in the air
const JUMP_VY = 30; // launch velocity off a ramp (scaled by speed)
/** Landing pitch tolerance: |pitch| within this is a clean landing. */
const CLEAN_PITCH = 0.42;
const TILT_RATE = 2.4; // radians/s the player can pitch the bike airborne
/** Ideal air pitch: nose slightly up carries momentum; flat is safe. */
const PITCH_GRAVITY = 1.1; // the bike noses down over time if untouched

// ---- Crash ------------------------------------------------------------------
const CRASH_SETTLE_FRAMES = 46; // tumble before the game-over card locks in

// ---- Scoring ----------------------------------------------------------------
const DIST_SCORE_PER_M = 1;
const CLEAN_LANDING_SCORE = 250;
const PASS_SCORE = 120;

// ---- Spawning ---------------------------------------------------------------
const MIN_GAP_M = 34; // minimum spacing between spawned hazards (eased by diff)
const SPAWN_AHEAD_M = 30; // spawn this far past the right edge (in meters)

type Phase = 'ready' | 'riding' | 'crashed' | 'game-over';
type ObKind = 'rock' | 'mud' | 'ramp' | 'rival';

interface Obstacle {
  kind: ObKind;
  /** World distance (meters) of this obstacle's left edge. */
  worldM: number;
  lane: number;
  /** Rivals drift; other obstacles are static in lane. */
  laneDrift?: number;
  /** Passed-scoring latch. */
  scored?: boolean;
  /** Width in meters for collision + draw. */
  wM: number;
}

/** Pixels per meter for the horizontal scroll (visual only). */
const PX_PER_M = 6;

export class TurboMotoInstance implements GameInstance {
  private readonly ctx: GameContext;

  private phase: Phase = 'ready';
  private tick = 0;
  private phaseEnteredTick = 0;

  // Motion.
  private distanceM = 0;
  private speed = BASE_SPEED;

  // Rider lane position.
  private lane = 1; // 0..LANE_COUNT-1
  private hopFrom = 1;
  private hopTo = 1;
  private hopLeft = 0; // frames remaining in a hop

  // Airborne state.
  private airborne = false;
  private airY = 0; // height above track (units)
  private airVy = 0;
  private pitch = 0; // radians; 0 = flat, + = nose up

  // Heat.
  private heat = 0;
  private stalled = false;
  private stallFrames = 0;

  // Score.
  private score = 0;
  private finalScore = 0;
  private cleanLandings = 0;

  // World.
  private obstacles: Obstacle[] = [];
  private lastSpawnM = 0;

  // Presentation.
  private crashFlash = 0;
  private shake = 0;
  private trailPhase = 0;

  // Latched inputs.
  private inUp = false;
  private inDown = false;
  private inBoost = false;
  private restartArmed = false;

  // Engine drone.
  private engine: LoopHandle | null = null;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    // Consume one PRNG draw so a fresh instance's stream is seeded even
    // if the first obstacle spawn is a few ticks out (parity with the
    // other cartridges' constructors).
    void this.ctx.random.next();
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'ready') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.startRun();
      }
      return;
    }
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    // Edge-triggered lane hop on the ground; airborne, up/down pitches.
    this.inUp = state.up || state.stickL.y < -0.4;
    this.inDown = state.down || state.stickL.y > 0.4;
    this.inBoost = state.fireB || state.fireA;

    if (this.phase === 'riding' && !this.airborne && this.hopLeft === 0) {
      if (state.justPressed.has('up')) this.hop(-1);
      else if (state.justPressed.has('down')) this.hop(1);
    }
  }

  update(_dt: number): void {
    const dt = 1 / 60;
    this.tick++;
    this.trailPhase += dt;
    if (this.crashFlash > 0) this.crashFlash = Math.max(0, this.crashFlash - dt * 3);
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 4);

    if (this.phase === 'ready') return;

    if (this.phase === 'crashed') {
      // Tumble settles, then the game-over card locks in.
      this.engine?.setGain(0);
      if (this.tick - this.phaseEnteredTick >= CRASH_SETTLE_FRAMES) {
        this.enterPhase('game-over');
        this.finalScore = Math.floor(this.score);
        this.ctx.events.emit('death', { remainingLives: 0 });
      }
      return;
    }
    if (this.phase !== 'riding') return;

    this.tickHeat(dt);
    this.tickSpeed(dt);
    this.tickHop();
    this.tickAir(dt);

    // Advance the world.
    this.distanceM += this.speed * dt;
    this.score += this.speed * dt * DIST_SCORE_PER_M;

    this.tickSpawns();
    this.tickObstacles(dt);
    if (!this.airborne) this.tickCollisions();

    this.tickEngineAudio();
  }

  render(g: CanvasRenderingContext2D): void {
    let restore = false;
    if (this.shake > 0) {
      const t = this.tick;
      const m = this.shake * 3;
      g.save();
      g.translate(Math.round(Math.sin(t * 12.9) * m), Math.round(Math.cos(t * 9.3) * m));
      restore = true;
    }

    this.drawSky(g);
    this.drawTrack(g);
    this.drawObstacles(g);
    this.drawRider(g);

    if (restore) g.restore();

    if (this.crashFlash > 0) {
      g.globalAlpha = Math.min(0.5, this.crashFlash);
      g.fillStyle = CRASH_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    this.drawHud(g);

    if (this.phase === 'ready') this.drawReadyCard(g);
    else if (this.phase === 'game-over') this.drawGameOverCard(g);
  }

  resize(_w: number, _h: number): void {}

  pause(): void {
    this.engine?.setGain(0);
  }

  resume(): void {}

  teardown(): void {
    this.engine?.stop();
    this.engine = null;
    this.obstacles = [];
  }

  getScore(): number {
    return Math.floor(this.score);
  }

  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }

  /** Cabinet convention: polled to know when to spin up a new run. */
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  // --------------------------------------------------------------------------
  // Simulation
  // --------------------------------------------------------------------------

  private startRun(): void {
    this.phase = 'riding';
    this.phaseEnteredTick = this.tick;
    this.distanceM = 0;
    this.speed = BASE_SPEED;
    this.lane = 1;
    this.hopFrom = 1;
    this.hopTo = 1;
    this.hopLeft = 0;
    this.airborne = false;
    this.airY = 0;
    this.airVy = 0;
    this.pitch = 0;
    this.heat = 0;
    this.stalled = false;
    this.stallFrames = 0;
    this.score = 0;
    this.cleanLandings = 0;
    this.obstacles = [];
    this.lastSpawnM = 0;
    if (!this.engine) {
      this.engine = this.ctx.audio.loop({ wave: 'sawtooth', f: 90, gain: 0, fm: { ratio: 1.5, index: 10 } });
    }
  }

  private difficulty(): number {
    return Math.min(MAX_DIFF, this.distanceM / RAMP_DIST);
  }

  private tickHeat(dt: number): void {
    if (this.stalled) {
      // Bleed heat; end the stall once cool enough AND the minimum has passed.
      this.heat = Math.max(0, this.heat - HEAT_COOL_PER_S * 1.6 * dt);
      this.stallFrames++;
      if (this.stallFrames >= STALL_MIN_FRAMES && this.heat <= STALL_HEAT_CLEAR) {
        this.stalled = false;
      }
      return;
    }
    if (this.inBoost) {
      this.heat = Math.min(HEAT_MAX, this.heat + HEAT_RISE_PER_S * dt);
      if (this.heat >= HEAT_MAX) {
        // Redline → engine stalls. Soft failure: bogs down, no boost.
        this.stalled = true;
        this.stallFrames = 0;
        this.ctx.audio.sfx('stall');
        this.shake = Math.max(this.shake, 0.5);
      }
    } else {
      this.heat = Math.max(0, this.heat - HEAT_COOL_PER_S * dt);
    }
  }

  private tickSpeed(dt: number): void {
    let target: number;
    if (this.stalled) target = STALL_SPEED;
    else if (this.inBoost) target = BOOST_SPEED;
    else target = BASE_SPEED;
    // Difficulty nudges cruising speed up so late runs feel faster.
    target *= 1 + this.difficulty() * 0.35;
    if (this.speed < target) this.speed = Math.min(target, this.speed + ACCEL * dt);
    else this.speed = Math.max(target, this.speed - ACCEL * dt);
  }

  private hop(dir: number): void {
    const to = this.lane + dir;
    if (to < 0 || to >= LANE_COUNT) return;
    this.hopFrom = this.lane;
    this.hopTo = to;
    this.lane = to;
    this.hopLeft = HOP_FRAMES;
    this.ctx.audio.sfx('hop');
  }

  private tickHop(): void {
    if (this.hopLeft > 0) this.hopLeft--;
  }

  /** Rider lane in continuous units (interpolated during a hop). */
  private laneF(): number {
    if (this.hopLeft === 0) return this.lane;
    const t = 1 - this.hopLeft / HOP_FRAMES;
    return this.hopFrom + (this.hopTo - this.hopFrom) * t;
  }

  private tickAir(dt: number): void {
    if (!this.airborne) return;
    // Player pitches the bike; gravity noses it down if untouched.
    if (this.inUp) this.pitch += TILT_RATE * dt;
    else if (this.inDown) this.pitch -= TILT_RATE * dt;
    else this.pitch -= PITCH_GRAVITY * dt * 0.5;
    this.pitch = Math.max(-1.4, Math.min(1.4, this.pitch));

    this.airVy -= GRAVITY * dt;
    this.airY += this.airVy * dt;
    if (this.airY <= 0) {
      this.airY = 0;
      this.airborne = false;
      this.resolveLanding();
    }
  }

  private launch(): void {
    this.airborne = true;
    // Faster = higher, but clamp so it stays readable.
    const boost = 0.7 + Math.min(1, this.speed / BOOST_SPEED) * 0.6;
    this.airVy = JUMP_VY * boost;
    this.airY = 0.01;
    this.pitch = 0.2; // a small nose-up off the lip feels right
    this.ctx.audio.sfx('launch');
  }

  private resolveLanding(): void {
    if (Math.abs(this.pitch) <= CLEAN_PITCH) {
      // Clean landing: reward + a little speed carry.
      this.cleanLandings++;
      this.score += CLEAN_LANDING_SCORE;
      this.speed = Math.min(BOOST_SPEED * 1.15, this.speed + 6);
      this.ctx.audio.sfx('land-clean');
      this.pitch = 0;
    } else {
      // Bad angle at speed → wipeout.
      this.crash();
    }
  }

  private tickSpawns(): void {
    const diff = this.difficulty();
    // As difficulty climbs, hazards pack tighter.
    const gap = MIN_GAP_M * (1 - diff * 0.4) + 8;
    const spawnEdgeM = this.distanceM + WIDTH / PX_PER_M + SPAWN_AHEAD_M;
    if (spawnEdgeM - this.lastSpawnM < gap) return;
    this.lastSpawnM = spawnEdgeM;

    const r = this.ctx.random;
    // Weight: more rocks/rivals as difficulty climbs; ramps stay common
    // (they're the fun). Mud is an occasional tempo-tax.
    const roll = r.next();
    let kind: ObKind;
    if (roll < 0.34) kind = 'ramp';
    else if (roll < 0.58) kind = 'rock';
    else if (roll < 0.78) kind = 'rival';
    else kind = 'mud';

    const lane = r.int(0, LANE_COUNT - 1);
    const wM = kind === 'ramp' ? 10 : kind === 'mud' ? 12 : 6;
    const ob: Obstacle = { kind, worldM: spawnEdgeM, lane, wM };
    if (kind === 'rival') ob.laneDrift = r.next() < 0.5 ? -1 : 1;
    this.obstacles.push(ob);
  }

  private tickObstacles(dt: number): void {
    for (const ob of this.obstacles) {
      if (ob.kind === 'rival' && ob.laneDrift !== undefined) {
        // Rivals crawl backward relative to the player and occasionally
        // switch lanes to be annoying. Deterministic: derived from tick.
        if ((this.tick + Math.floor(ob.worldM)) % 90 === 0) {
          const next = ob.lane + ob.laneDrift;
          if (next < 0 || next >= LANE_COUNT) ob.laneDrift *= -1;
          else ob.lane = next;
        }
        ob.worldM -= 6 * dt; // slower than the world; player overtakes
      }
      // Pass-scoring: the moment an obstacle's right edge slips behind the
      // rider without a collision, award a pass (once).
      const rightM = ob.worldM + ob.wM;
      if (!ob.scored && rightM < this.distanceM + RIDER_X / PX_PER_M) {
        ob.scored = true;
        if (ob.kind === 'rival') {
          this.score += PASS_SCORE;
        }
      }
    }
    // Cull anything fully off the left edge.
    const cullM = this.distanceM - 12;
    this.obstacles = this.obstacles.filter((o) => o.worldM + o.wM > cullM);
  }

  private tickCollisions(): void {
    const riderLeftM = this.distanceM + RIDER_X / PX_PER_M;
    const riderRightM = riderLeftM + 5;
    const laneF = this.laneF();
    for (const ob of this.obstacles) {
      if (Math.abs(ob.lane - laneF) > 0.5) continue;
      const obLeft = ob.worldM;
      const obRight = ob.worldM + ob.wM;
      if (riderRightM < obLeft || riderLeftM > obRight) continue;
      // Overlap in this lane.
      switch (ob.kind) {
        case 'ramp':
          this.launch();
          return;
        case 'mud':
          // Tempo-tax: bleed speed, no crash.
          this.speed = Math.max(STALL_SPEED, this.speed - 40 * (1 / 60));
          this.shake = Math.max(this.shake, 0.2);
          break;
        case 'rock':
        case 'rival':
          this.crash();
          return;
      }
    }
  }

  private crash(): void {
    this.enterPhase('crashed');
    this.crashFlash = 0.5;
    this.shake = 1;
    this.airborne = false;
    this.airY = 0;
    this.ctx.audio.sfx('crash');
  }

  private enterPhase(p: Phase): void {
    this.phase = p;
    this.phaseEnteredTick = this.tick;
  }

  private tickEngineAudio(): void {
    if (!this.engine) return;
    const frac = this.speed / BOOST_SPEED;
    this.engine.setFrequency(80 + 220 * frac + (this.inBoost && !this.stalled ? 40 : 0));
    this.engine.setGain(0.1 + 0.12 * frac + (this.stalled ? -0.06 : 0));
  }

  // --------------------------------------------------------------------------
  // Rendering
  // --------------------------------------------------------------------------

  private drawSky(g: CanvasRenderingContext2D): void {
    const grad = g.createLinearGradient(0, 0, 0, TRACK_TOP);
    grad.addColorStop(0, SKY_TOP);
    grad.addColorStop(1, SKY_BOTTOM);
    g.fillStyle = grad;
    g.fillRect(0, 0, WIDTH, TRACK_TOP);

    // Low sun disc.
    g.fillStyle = SUN;
    g.beginPath();
    g.arc(WIDTH * 0.72, TRACK_TOP - 6, 34, Math.PI, Math.PI * 2);
    g.fill();

    // Parallax ridges (scroll at a fraction of the track).
    const p1 = (this.distanceM * PX_PER_M * 0.15) % WIDTH;
    const p2 = (this.distanceM * PX_PER_M * 0.28) % WIDTH;
    this.drawRidge(g, HILL_FAR, TRACK_TOP - 4, 18, p1);
    this.drawRidge(g, HILL_NEAR, TRACK_TOP - 1, 12, p2);
  }

  private drawRidge(g: CanvasRenderingContext2D, color: string, baseY: number, amp: number, off: number): void {
    g.fillStyle = color;
    g.beginPath();
    g.moveTo(0, baseY);
    for (let x = 0; x <= WIDTH; x += 24) {
      const y = baseY - amp * (0.5 + 0.5 * Math.sin((x + off) * 0.03));
      g.lineTo(x, y);
    }
    g.lineTo(WIDTH, baseY);
    g.closePath();
    g.fill();
  }

  private drawTrack(g: CanvasRenderingContext2D): void {
    // Alternating lane bands.
    for (let i = 0; i < LANE_COUNT; i++) {
      g.fillStyle = i % 2 === 0 ? TRACK_BASE : TRACK_DARK;
      g.fillRect(0, TRACK_TOP + i * LANE_H, WIDTH, LANE_H);
    }
    // Lane separators.
    g.strokeStyle = LANE_LINE;
    g.lineWidth = 1;
    for (let i = 1; i < LANE_COUNT; i++) {
      const y = TRACK_TOP + i * LANE_H;
      g.beginPath();
      g.moveTo(0, y + 0.5);
      g.lineTo(WIDTH, y + 0.5);
      g.stroke();
    }
    // Rumble strips glow at top + bottom of the track.
    g.fillStyle = TRACK_EDGE;
    g.fillRect(0, TRACK_TOP - 2, WIDTH, 2);
    g.fillRect(0, TRACK_BOTTOM, WIDTH, 2);
    // Scrolling dashed center hazard stripe on each lane.
    const dashOff = (this.distanceM * PX_PER_M) % 40;
    g.fillStyle = TRACK_STRIPE;
    for (let i = 0; i < LANE_COUNT; i++) {
      const y = TRACK_TOP + i * LANE_H + LANE_H / 2 - 1;
      for (let x = -dashOff; x < WIDTH; x += 40) {
        g.fillRect(x, y, 18, 2);
      }
    }
  }

  private laneY(laneF: number): number {
    return TRACK_TOP + (laneF + 0.5) * LANE_H;
  }

  private worldToScreenX(worldM: number): number {
    return (worldM - this.distanceM) * PX_PER_M;
  }

  private drawObstacles(g: CanvasRenderingContext2D): void {
    for (const ob of this.obstacles) {
      const x = this.worldToScreenX(ob.worldM);
      const w = ob.wM * PX_PER_M;
      if (x + w < 0 || x > WIDTH) continue;
      const y = this.laneY(ob.lane);
      switch (ob.kind) {
        case 'ramp': {
          g.fillStyle = RAMP_FACE;
          g.beginPath();
          g.moveTo(x, y + 10);
          g.lineTo(x + w, y - 12);
          g.lineTo(x + w, y + 10);
          g.closePath();
          g.fill();
          g.fillStyle = RAMP_EDGE;
          g.fillRect(x + w - 3, y - 13, 3, 24);
          break;
        }
        case 'mud': {
          g.fillStyle = MUD;
          g.fillRect(x, y - 6, w, 12);
          break;
        }
        case 'rock': {
          g.fillStyle = ROCK_DARK;
          g.fillRect(x, y - 2, w, 10);
          g.fillStyle = ROCK;
          g.fillRect(x, y - 8, w, 8);
          break;
        }
        case 'rival': {
          this.drawBike(g, x, y, RIVAL_BODY, RIVAL_DARK, RIVAL_HELMET, 0, false);
          break;
        }
      }
    }
  }

  private drawRider(g: CanvasRenderingContext2D): void {
    const laneF = this.laneF();
    const y = this.laneY(laneF) - this.airY * 2.2;
    // Motion trail while boosting.
    if (this.inBoost && !this.stalled && this.phase === 'riding') {
      g.fillStyle = RIDER_TRAIL;
      g.globalAlpha = 0.4;
      for (let i = 1; i <= 3; i++) {
        g.fillRect(RIDER_X - i * 6, y - 4, 4, 9);
      }
      g.globalAlpha = 1;
    }
    this.drawBike(g, RIDER_X, y, RIDER_BODY, RIDER_DARK, RIDER_HELMET, this.airborne ? this.pitch : 0, true);
  }

  private drawBike(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    body: string,
    dark: string,
    helmet: string,
    pitch: number,
    isPlayer: boolean
  ): void {
    g.save();
    g.translate(x + 15, y + 2);
    g.rotate(-pitch);
    // wheels
    g.fillStyle = RIDER_WHEEL;
    g.beginPath();
    g.arc(-10, 4, 5, 0, Math.PI * 2);
    g.arc(10, 4, 5, 0, Math.PI * 2);
    g.fill();
    // frame
    g.fillStyle = dark;
    g.fillRect(-10, -2, 20, 5);
    g.fillStyle = body;
    g.fillRect(-8, -4, 16, 4);
    // rider torso + helmet
    g.fillStyle = body;
    g.fillRect(-3, -12, 6, 9);
    g.fillStyle = helmet;
    g.beginPath();
    g.arc(0, -13, 4, 0, Math.PI * 2);
    g.fill();
    if (isPlayer) {
      // visor stripe
      g.fillStyle = '#0a0a12';
      g.fillRect(-3, -14, 6, 2);
    }
    g.restore();
  }

  // --------------------------------------------------------------------------
  // HUD + cards
  // --------------------------------------------------------------------------

  private drawHud(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    // Score (left).
    g.textAlign = 'left';
    g.fillStyle = HUD_LABEL;
    g.fillText('SCORE', 8, 6);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 18);

    // Distance (center).
    g.textAlign = 'center';
    g.fillStyle = HUD_LABEL;
    g.fillText('DIST', WIDTH / 2, 6);
    g.fillStyle = HUD_DIST;
    g.fillText(`${Math.floor(this.distanceM)}m`, WIDTH / 2, 18);

    // Speed (right).
    g.textAlign = 'right';
    g.fillStyle = HUD_LABEL;
    g.fillText('SPEED', WIDTH - 8, 6);
    g.fillStyle = HUD_SPEED;
    g.font = 'bold 14px ui-monospace, monospace';
    g.fillText(String(Math.floor(this.speed * 3.6)), WIDTH - 8, 18);

    this.drawHeatGauge(g);
    g.textAlign = 'left';
  }

  private drawHeatGauge(g: CanvasRenderingContext2D): void {
    // Centered temperature bar below the DIST readout.
    const barW = 120;
    const barH = 8;
    const barX = (WIDTH - barW) / 2;
    const barY = 36;
    const frac = this.heat / HEAT_MAX;

    g.strokeStyle = HEAT_FRAME;
    g.lineWidth = 1;
    g.strokeRect(barX - 0.5, barY - 0.5, barW + 1, barH + 1);

    // Fill hue sweeps cool → warm → hot → redline.
    let fill: string;
    if (this.stalled) fill = HEAT_REDLINE;
    else if (frac < 0.5) fill = HEAT_COOL;
    else if (frac < 0.8) fill = HEAT_WARM;
    else fill = HEAT_HOT;
    g.fillStyle = fill;
    g.fillRect(barX, barY, Math.round(barW * frac), barH);

    // Redline tick at 100%.
    g.fillStyle = HEAT_REDLINE;
    g.fillRect(barX + barW - 2, barY - 2, 2, barH + 4);

    g.font = '8px ui-monospace, monospace';
    g.textAlign = 'center';
    g.fillStyle = this.stalled ? HEAT_REDLINE : HEAT_FRAME;
    g.fillText(this.stalled ? 'OVERHEAT!' : 'HEAT', WIDTH / 2, barY + barH + 2);
  }

  private drawCard(g: CanvasRenderingContext2D, title: string, sub: string, cta: string): void {
    const w = 300;
    const h = 108;
    const x = (WIDTH - w) / 2;
    const y = (HEIGHT - h) / 2;
    g.fillStyle = CARD_BG;
    g.fillRect(x, y, w, h);
    g.strokeStyle = CARD_BORDER;
    g.lineWidth = 1;
    g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = title === 'TURBO MOTO 84' ? ATTRACT_TITLE : CARD_TEXT;
    g.font = 'bold 20px ui-monospace, monospace';
    g.fillText(title, WIDTH / 2, y + 32);

    g.fillStyle = CARD_SUB;
    g.font = '11px ui-monospace, monospace';
    g.fillText(sub, WIDTH / 2, y + 60);

    g.fillStyle = ATTRACT_PROMPT;
    g.font = '9px ui-monospace, monospace';
    if ((this.tick - this.phaseEnteredTick) % 40 < 28) {
      g.fillText(cta, WIDTH / 2, y + 84);
    }
    g.textAlign = 'left';
    g.textBaseline = 'top';
  }

  private drawReadyCard(g: CanvasRenderingContext2D): void {
    this.drawCard(
      g,
      'TURBO MOTO 84',
      'endless dirt · hop lanes · watch the heat',
      'PRESS FIRE TO RIDE'
    );
  }

  private drawGameOverCard(g: CanvasRenderingContext2D): void {
    this.drawCard(
      g,
      'WIPEOUT',
      `${Math.floor(this.distanceM)}m · SCORE ${this.finalScore}`,
      'PRESS FIRE TO RIDE AGAIN'
    );
  }
}
